import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Wind, X, Clock } from 'lucide-react';
import { breathingGroups as fallbackBreathingGroups } from '../data/mockData';
import { useContent } from '../context/ContentContext';
import BreathingCircle from '../components/BreathingCircle';
import ShareSheet from '../components/ShareSheet';
import ContentReactBar from '../components/ContentReactBar';
import CheckInModal from '../components/CheckInModal';
import { logMindfulSession } from '../utils/mindful';

const formatPattern = (ex) =>
  ex.hold
    ? `${ex.inhale}s · hold ${ex.hold}s · ${ex.exhale}s`
    : `${ex.inhale}s in · ${ex.exhale}s out`;

const buildBreathingShareContent = (ex) => {
  if (!ex) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return {
    kind: 'breathing',
    id: ex.id,
    title: ex.name,
    text: `"${ex.name}" (${formatPattern(ex)}) — a breath pattern on Reflections by Rowdy`,
    url: `${origin}/breathing?exercise=${ex.id}`,
    recommendable: true,
  };
};

const BreathingExercisesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { breathingGroups, getBreathingExerciseById } = useContent();
  const groups = breathingGroups && breathingGroups.length ? breathingGroups : fallbackBreathingGroups;
  const activeId = searchParams.get('exercise');
  const active = activeId ? getBreathingExerciseById(activeId) : null;
  const [shareExercise, setShareExercise] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Lock body scroll while overlay is open, track session start for mindful logging
  const sessionStartRef = useRef(null);
  useEffect(() => {
    if (active) {
      document.body.style.overflow = 'hidden';
      sessionStartRef.current = Date.now();
    } else {
      document.body.style.overflow = '';
      sessionStartRef.current = null;
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [active]);

  const openExercise = (id) => {
    setSearchParams({ exercise: id }, { replace: false });
  };
  const [checkIn, setCheckIn] = useState(null); // ref string when modal open

  const closeExercise = () => {
    // Log session if user spent at least 15 seconds in the exercise
    if (active && sessionStartRef.current) {
      const dur = Math.round((Date.now() - sessionStartRef.current) / 1000);
      if (dur >= 15) {
        logMindfulSession({ kind: 'breath', durationSec: dur, ref: active.id });
        // Only prompt the check-in when they actually breathed for a bit —
        // otherwise it feels intrusive.
        setCheckIn(`breath:${active.id}`);
      }
    }
    const next = new URLSearchParams(searchParams);
    next.delete('exercise');
    setSearchParams(next, { replace: true });
  };

  const totalCount = groups.reduce((s, g) => s + g.exercises.length, 0);

  return (
    <div
      className="animate-fade-in pb-8 min-h-screen"
      data-testid="breathing-page"
      style={{
        // Sage → deep-forest wash, kept intentionally quiet so the breath
        // animations remain the only thing that moves.
        background:
          'linear-gradient(180deg, #F0F4EE 0%, #DFE8DE 45%, #B7CFC0 100%)',
      }}
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#5B7A65] hover:text-[#173F35] transition-colors duration-300 mb-6"
          data-testid="breathing-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md"
            style={{
              background: 'linear-gradient(135deg, #173F35 0%, #245549 100%)',
              boxShadow: '0 6px 18px rgba(23,63,53,0.25)',
            }}
          >
            <Wind className="w-7 h-7 text-[#F8F6F0]" />
          </div>
          <div>
            <h1
              className="text-2xl font-bold"
              style={{ fontFamily: "'Playfair Display', serif", color: '#173F35' }}
            >
              Breathing Exercises
            </h1>
            <p className="text-sm text-[#5B7A65]">
              {totalCount} animated circle timers · 6 categories
            </p>
          </div>
        </div>
      </div>

      {/* Groups */}
      <div className="px-5 space-y-8">
        {groups.map((group, gi) => (
          <section
            key={group.id}
            className="animate-fade-in-up"
            style={{ animationDelay: `${gi * 0.05}s` }}
            data-testid={`breathing-group-${group.id}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: group.accent, boxShadow: `0 0 10px ${group.accent}80` }}
              />
              <h2
                className="text-base font-semibold text-stone-800 uppercase tracking-wider"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {group.name}
              </h2>
            </div>
            <p className="text-xs text-stone-500 mb-4 ml-4">{group.description}</p>

            <div className="grid grid-cols-1 gap-2.5">
              {group.exercises.map((ex) => (
                <div key={ex.id} className="relative">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openExercise(ex.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openExercise(ex.id); } }}
                    className="w-full flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3.5 shadow-md hover:shadow-lg hover:border-[#D6AD60]/30 transition-all duration-300 group text-left active:scale-[0.99] cursor-pointer"
                    data-testid={`breathing-exercise-${ex.id}`}
                  >
                    {/* Mini orb preview */}
                    <div
                      className="w-11 h-11 rounded-full flex-shrink-0 shadow-inner relative overflow-hidden"
                      style={{ backgroundColor: group.accent, opacity: 0.9 }}
                    >
                      <div
                        className="absolute inset-1 rounded-full bg-white/25"
                        style={{
                          animation: `breath-preview ${(ex.inhale + (ex.hold || 0) + ex.exhale)}s ease-in-out infinite`,
                        }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-stone-800 group-hover:text-[#D6AD60] transition-colors line-clamp-1">
                        {ex.name}
                      </h3>
                      <p className="text-[11px] text-stone-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatPattern(ex)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-500 group-hover:text-[#D6AD60] transition-colors">
                        Start
                      </span>
                      <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                        <ContentReactBar
                          contentRef={`breath:${ex.id}`}
                          variant="onLight"
                          size="sm"
                          share={{
                            kind: 'breathing',
                            id: ex.id,
                            title: ex.name,
                            text: `Try "${ex.name}" — a breathing exercise on Reflections by Rowdy`,
                            url: typeof window !== 'undefined' ? `${window.location.origin}/breathing?exercise=${ex.id}` : '',
                            recommendable: true,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Overlay timer */}
      {active && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-stone-900/85 backdrop-blur-sm animate-fade-in"
          data-testid="breathing-overlay"
        >
          <div className="relative w-full max-w-md mx-4">
            <button
              onClick={closeExercise}
              className="absolute -top-2 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              data-testid="breathing-close-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2 ml-1">
              {active.groupName}
            </p>
            <BreathingCircle
              pattern={{ inhale: active.inhale, hold: active.hold || 0, exhale: active.exhale }}
              title={active.name}
            />
          </div>
        </div>
      )}

      {/* Inline keyframes for preview orb */}
      <style>{`
        @keyframes breath-preview {
          0%, 100% { transform: scale(0.6); opacity: 0.5; }
          50% { transform: scale(1); opacity: 0.95; }
        }
      `}</style>

      <ShareSheet
        open={!!shareExercise}
        onClose={() => setShareExercise(null)}
        content={buildBreathingShareContent(shareExercise)}
      />
      <CheckInModal
        open={!!checkIn}
        onClose={() => setCheckIn(null)}
        contentRef={checkIn}
        title="How are you feeling now?"
      />
    </div>
  );
};

export default BreathingExercisesPage;
